import {
	useCompleteRecommendationMutation,
	useDeleteProductMutation,
	useGetProductQuery,
} from '@codezo/graphql';
import { formatDate, formatMonthYear } from '../../lib/utils';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Image as ImageIcon, Pencil, Ruler, Sparkles } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useQueryParam } from '../../hooks/useQueryParam';
import { useSetPageMeta } from '../../hooks/useSetPageMeta';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { ImageViewer } from '../../components/ui/image-viewer';
import { ProductImage } from '../../components/ui/product-image';
import { SectionCard } from '../../components/ui/section-card';
import { Tabs } from '../../components/ui/tabs';
import { DetailRow } from '../../components/ui/detail-row';
import { SpecificationsView, buildSpecificationRows } from '@codezo/product-specs';
import { AddSpecificationsModal, type SpecificationsData } from './AddSpecificationsModal';
import { CommunitySection } from './CommunitySection';
import { EditProductModal, type EditProductTab } from './EditProductModal';
import { EnhanceProductModal } from './EnhanceProductModal';
import { ProductPricesSection } from './ProductPricesSection';
import { ProductSamplePricesSection } from './ProductSamplePricesSection';

/* ── Loading skeleton ── */

function DetailSkeleton() {
	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8">
			<div className="mb-5 h-4 w-20 animate-pulse rounded bg-surface-fill" />
			<div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.2fr]">
				<div className="aspect-square animate-pulse rounded-[var(--r-3)] bg-surface-fill" />
				<div className="space-y-4">
					<div className="h-8 w-3/4 animate-pulse rounded bg-surface-fill" />
					<div className="h-4 w-1/2 animate-pulse rounded bg-surface-fill" />
					<div className="h-32 animate-pulse rounded bg-surface-fill" />
				</div>
			</div>
		</div>
	);
}

/* ════════════════════════ PRODUCT DETAIL PAGE ════════════════════════ */

export function ProductDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [tab, setTab] = useQueryParam('tab', 'overview');
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [editOpen, setEditOpen] = useState(false);
	const [editTab, setEditTab] = useState<EditProductTab>('details');
	const [specsOpen, setSpecsOpen] = useState(false);
	const [enhanceOpen, setEnhanceOpen] = useState(false);

	const { data, loading, refetch } = useGetProductQuery({
		variables: { id: id! },
		fetchPolicy: 'no-cache',
		skip: !id,
	});

	const product = data?.getProduct;
	useSetPageMeta(product?.name || 'Product', [
		{ label: 'Products', path: '/products' },
		{ label: product?.name || 'Loading...' },
	]);

	const openEdit = useCallback((nextTab: EditProductTab) => {
		setEditTab(nextTab);
		setEditOpen(true);
	}, []);

	const [deleteProduct, { loading: deleting }] = useDeleteProductMutation();
	const [completeRecommendation, { loading: completingRec }] =
		useCompleteRecommendationMutation();
	const handleDelete = async () => {
		if (!product) return;
		try {
			await deleteProduct({ variables: { input: { id: product.id } } });
			toast.success('Product deleted');
			navigate('/products');
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to delete product');
		}
	};

	const handleAddSpecifications = useCallback(
		async (data: SpecificationsData) => {
			const rec = product?.recommendations?.find(
				(r) => r.type === 'MISSING_SPECIFICATIONS' && r.status === 'ACTIVE'
			);
			if (!rec) return;
			try {
				const result = await completeRecommendation({
					variables: {
						input: {
							recommendationId: rec.id,
							dimensions: {
								length: data.dimensions.length,
								width: data.dimensions.width,
								height: data.dimensions.height,
								unit: data.dimensions.unit,
							},
							weight: data.weight,
						},
					},
				});
				if (result.data?.completeRecommendation?.success) {
					toast.success('Specifications added');
					setSpecsOpen(false);
					refetch();
				}
			} catch (err) {
				toast.error(err instanceof Error ? err.message : 'Failed to add specifications');
			}
		},
		[product?.recommendations, completeRecommendation, refetch]
	);

	const handleEnhanceProduct = useCallback(async () => {
		const rec = product?.recommendations?.find(
			(r) => r.type === 'ENHANCE_WITH_STUDIO' && r.status === 'ACTIVE'
		);
		if (!rec) return;
		try {
			const result = await completeRecommendation({
				variables: { input: { recommendationId: rec.id } },
			});
			if (result.data?.completeRecommendation?.success) {
				toast.success('Product enhanced');
				setEnhanceOpen(false);
				refetch();
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to enhance product');
		}
	}, [product?.recommendations, completeRecommendation, refetch]);

	const handleRecommendationAction = useCallback((type: string) => {
		if (type === 'MISSING_SPECIFICATIONS') setSpecsOpen(true);
		else if (type === 'ENHANCE_WITH_STUDIO') setEnhanceOpen(true);
	}, []);

	if (loading || !product) {
		return <DetailSkeleton />;
	}

	const activeRecommendations =
		product.recommendations?.filter((r) => r.status === 'ACTIVE') ?? [];

	const specRows = buildSpecificationRows({
		specifications: product.specifications,
		dimensions: product.dimensions,
		weight: product.weight,
	});

	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8">
			{/* ── Back ── */}
			<button
				onClick={() => navigate(-1)}
				className="mb-4 flex items-center gap-1.5 bg-transparent text-sm text-text-3 transition-colors hover:text-text-1"
			>
				<ArrowLeft size={14} />
				Products
			</button>

			{/* ── Main layout: images left, info right ── */}
			<div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.2fr]">
				{/* Left: Image gallery */}
				<div>
					<ImageViewer images={product.images ?? []} />
				</div>

				{/* Right: Product info */}
				<div>
					{/* Header */}
					<div className="mb-6 flex items-start justify-between gap-4">
						<div>
							<h1 className="mb-2 text-2xl font-medium leading-tight -tracking-[0.02em] text-text-1 sm:text-3xl">
								{product.name}
							</h1>
							<div className="flex flex-wrap items-center gap-3 text-base text-text-3">
								{product.sku && (
									<span className="font-mono text-text-4">{product.sku}</span>
								)}
								{product.createdAt && (
									<>
										<span className="text-text-4">·</span>
										<span>Created {formatMonthYear(product.createdAt)}</span>
									</>
								)}
							</div>
						</div>
						<div className="flex shrink-0 gap-2">
							<Button variant="outline" size="sm" onClick={() => openEdit('details')}>
								<Pencil size={14} data-icon="inline-start" />
								Edit
							</Button>
							<Button
								variant="destructive"
								size="sm"
								onClick={() => setDeleteOpen(true)}
							>
								Delete
							</Button>
						</div>
					</div>

					{/* Tabs */}
					<div className="mb-6">
						<Tabs
							value={tab}
							onChange={setTab}
							items={[
								{ value: 'overview', label: 'Overview' },
								{
									value: 'images',
									label: 'Images',
									count: product.images?.length ?? 0,
								},
							]}
						/>
					</div>

					{/* ── Overview tab ── */}
					{tab === 'overview' && (
						<div className="flex flex-col gap-4">
							{/* Description */}
							<SectionCard title="Description">
								<div className="px-4 py-3 text-base leading-relaxed text-text-2">
									{product.description || (
										<span className="text-text-4">
											No description added yet.
										</span>
									)}
								</div>
							</SectionCard>

							{/* Details */}
							<SectionCard title="Details">
								<div className="flex flex-col">
									<DetailRow label="SKU" value={product.sku || '—'} />
									{product.brand && (
										<DetailRow label="Brand" value={product.brand.name} />
									)}
									<DetailRow
										label="Created"
										value={
											product.createdAt ? formatDate(product.createdAt) : '—'
										}
									/>
									<DetailRow
										label="Updated"
										value={
											product.updatedAt ? formatDate(product.updatedAt) : '—'
										}
										last
									/>
								</div>
							</SectionCard>

							{/* Community page — house products only; a client's product is never
							    shown to another client. */}
							{!product.organizationId && (
								<CommunitySection product={product} onChanged={refetch} />
							)}

							{/* Specifications */}
							<SectionCard
								title="Specifications"
								action={
									specRows.length > 0 ? (
										<button onClick={() => openEdit('specifications')}>
											<Pencil size={12} />
											Edit
										</button>
									) : undefined
								}
							>
								{specRows.length > 0 ? (
									<SpecificationsView rows={specRows} />
								) : (
									<div className="flex flex-col items-center gap-3 py-10 text-center">
										<div className="flex h-9 w-9 items-center justify-center rounded-full border border-line-1 bg-surface-fill text-text-3">
											<Ruler size={16} />
										</div>
										<div className="text-sm text-text-3">
											No specifications added yet
										</div>
										<Button
											variant="outline"
											size="sm"
											onClick={() => openEdit('specifications')}
										>
											Add specifications
										</Button>
									</div>
								)}
							</SectionCard>

							{/* Catalogue prices — on Overview rather than behind a tab: what a
							    product costs is part of reading the product, not a separate
							    errand. */}
							<ProductPricesSection productId={product.id} />
							<ProductSamplePricesSection productId={product.id} />

							{/* Studio file link */}
							{product.studioFile && (
								<SectionCard title="Design file">
									<div className="flex items-center gap-3 px-4 py-3">
										<div className="flex h-9 w-9 items-center justify-center rounded-[var(--r-2)] border border-line-1 bg-surface-fill">
											<ImageIcon size={16} className="text-text-3" />
										</div>
										<div className="min-w-0 flex-1">
											<div className="text-base font-medium text-text-1">
												{product.studioFile.name}
											</div>
											{product.studioFile.currentVersion && (
												<div className="text-xs text-text-4">
													Version{' '}
													{
														product.studioFile.currentVersion
															.versionNumber
													}
												</div>
											)}
										</div>
									</div>
								</SectionCard>
							)}

							{/* Recommendations */}
							{activeRecommendations.length > 0 && (
								<SectionCard
									title={`Recommendations (${activeRecommendations.length})`}
								>
									<div className="flex flex-col">
										{activeRecommendations.map((rec, i) => {
											const isSpecs = rec.type === 'MISSING_SPECIFICATIONS';
											const Icon = isSpecs ? Ruler : Sparkles;

											return (
												<div
													key={rec.id}
													className="flex items-start gap-3.5 px-4 py-3.5"
													style={{
														borderBottom:
															i < activeRecommendations.length - 1
																? '0.5px solid var(--line-1)'
																: undefined,
													}}
												>
													<div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent-soft">
														<Icon size={15} className="text-accent" />
													</div>
													<div className="min-w-0 flex-1">
														<div className="mb-0.5 text-base font-medium text-text-1">
															{rec.title}
														</div>
														<div className="mb-3 text-sm leading-relaxed text-text-3">
															{rec.description}
														</div>
														<Button
															size="sm"
															onClick={() =>
																handleRecommendationAction(rec.type)
															}
															disabled={completingRec}
														>
															{rec.actionLabel}
															<ArrowRight
																size={12}
																data-icon="inline-end"
															/>
														</Button>
													</div>
												</div>
											);
										})}
									</div>
								</SectionCard>
							)}
						</div>
					)}

					{/* ── Images tab ── */}
					{tab === 'images' && (
						<SectionCard title={`All images (${product.images?.length ?? 0})`}>
							{!product.images || product.images.length === 0 ? (
								<div className="flex flex-col items-center gap-3 py-14 text-center">
									<div className="flex h-11 w-11 items-center justify-center rounded-full border border-line-1 bg-surface-fill text-text-3">
										<ImageIcon size={20} />
									</div>
									<div className="text-base text-text-2">No images</div>
									<div className="text-sm text-text-4">
										Upload images to showcase this product.
									</div>
								</div>
							) : (
								<div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3">
									{product.images.map((img) => (
										<div
											key={img.id}
											className="group relative overflow-hidden rounded-[var(--r-3)] border border-line-1 bg-surface-fill"
										>
											{img.url ? (
												<ProductImage
													image={img}
													className="aspect-square w-full"
												/>
											) : (
												<div className="flex aspect-square items-center justify-center">
													<ImageIcon size={20} className="text-text-4" />
												</div>
											)}
											{img.isPrimary && (
												<span className="absolute top-2 left-2 rounded-full bg-accent px-2 py-0.5 text-2xs font-medium text-accent-fg">
													Primary
												</span>
											)}
											<div className="px-2.5 py-2">
												<div className="truncate text-xs text-text-2">
													{img.name}
												</div>
											</div>
										</div>
									))}
								</div>
							)}
						</SectionCard>
					)}
				</div>
			</div>

			{/* ── Delete confirm ── */}
			<ConfirmDialog
				open={deleteOpen}
				onOpenChange={setDeleteOpen}
				title="Delete product"
				description={`Are you sure you want to delete "${product.name}"? This action cannot be undone.`}
				onConfirm={handleDelete}
				loading={deleting}
			/>

			<AddSpecificationsModal
				open={specsOpen}
				onOpenChange={setSpecsOpen}
				onSubmit={handleAddSpecifications}
				loading={completingRec}
				initialValues={{
					length: product.dimensions?.length,
					width: product.dimensions?.width,
					height: product.dimensions?.height,
					weight: product.weight,
					unit: product.dimensions?.unit,
				}}
			/>

			<EnhanceProductModal
				open={enhanceOpen}
				onOpenChange={setEnhanceOpen}
				onConfirm={handleEnhanceProduct}
				loading={completingRec}
				productName={product.name}
				originalImages={
					product.images
						?.filter((img) => img.url)
						.map((img) => ({ url: img.url!, isPrimary: img.isPrimary ?? false })) ?? []
				}
				aiPreviewImage={product.studioFile?.currentVersion?.imageUrls?.[0]}
			/>

			{id && (
				<EditProductModal
					open={editOpen}
					onOpenChange={setEditOpen}
					productId={id}
					initialTab={editTab}
					onSaved={refetch}
				/>
			)}
		</div>
	);
}
